/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { EnhancedPaper, Icon, showNotification } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import { Box, Button, Divider, Text, Group, Stack, Title, Tabs, ScrollArea } from 'summon-ui/mantine'
import { useQueryClient } from '@tanstack/react-query'
import { graphqlClient } from '@api/config'
import SocialSharingModal from '@components/SocialSharingModal/SocialSharingModal'
import { TENANT_CONFIG } from '@config'
import { useOnboardingStorage } from '@features/onboarding/onboarding.store'
import { InventoryItem, useEquipInventoryItemsMutation } from '@generated/generates'
import { useInventoryItems } from '@hooks'
import useAvatarDownloader from '@hooks/useAvatarDownloader'
import useEquipItems from '@hooks/useEquipItems'
import { DNA_TRAITS_SLOT_TYPES } from '@hooks/useInventoryItems'
import AvatarPreview from '@modules/avatar'
import { useCameraStorage } from '@modules/avatar/store/camera.store'
import { SlotType, useAvatarStore } from '@modules/inventory/store/avatar.store'
import InventorySlot from './InventorySlot'
import MobileInventory from './MobileInventory'
import { VARIANT_COLORS } from './inventory.constants'

const DNA_SLOT_TYPES = [SlotType.Body]

type InventoryModuleProps = {
  isDNA?: boolean
  isDNATraits?: boolean
  isMobile?: boolean
  hideActionMenu?: boolean
}

const InventoryModule = forwardRef(
  ({ isDNA = false, isDNATraits = false, isMobile = false, hideActionMenu }: InventoryModuleProps, ref: any) => {
    const t = useTranslation()
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const selectedElementRef = useRef(null)
    const [searchParams, setSearchParams] = useSearchParams()
    const { equipItem, unequipItem, previewItems, setRotating, resetPreviewItems, clearCategory } = useAvatarStore()
    const { setSelectedOnboardingSkin, selectedOnboardingSkin } = useOnboardingStorage()
    const [isEquipSuccess, setIsEquipSuccess] = useState(false)
    const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null)
    const { inventoryItems: items, isInventoryItemsLoading } = useInventoryItems({
      isDNA: isDNA,
      isDNATraits: isDNATraits
    })
    const { equipItemsPayload } = useEquipItems({})
    const isAbleToShareAvatar = TENANT_CONFIG.features.enableShareAvatar
    const { uploadAvatar } = useAvatarDownloader()
    const { mutateAsync: equipInventoryItemsBatch, isPending: isEquipping } = useEquipInventoryItemsMutation(
      graphqlClient(),
      {
        onSuccess: async () => {
          queryClient.invalidateQueries({ queryKey: ['GetInventoryItems'] })
          queryClient.invalidateQueries({ queryKey: ['GetInventoryItemsByIds'] })
          setRotating(true)
          setIsEquipSuccess(true)
          uploadAvatar(ref?.current, 2000)
        },
        onError: (data: unknown) => {
          // @ts-ignore
          console.error('ERROR equipping inventory Items: ', data?.response?.data?.message)
          showNotification({
            variant: 'danger',
            message: 'ERROR equipping inventory Items'
          })
        }
      }
    )
    useEffect(() => () => resetPreviewItems(), [])

    const groupedItems: { [key: string]: InventoryItem[] } = useMemo(() => {
      const countedItems = (isDNA ? items : previewItems)?.filter((item) =>
        isDNA ? DNA_SLOT_TYPES.includes(item.slotType as SlotType) : item.slotType !== SlotType.Body
      )

      // Count occurrences of each item ID
      const counts = countedItems.reduce((acc, item) => {
        const itemId = item.id
        //@ts-ignore
        acc[itemId] = (acc[itemId] || 0) + 1
        return acc
      }, {})

      // Group items by slot type, taking count into consideration
      const groupedItems: { [key: string]: InventoryItem[] } = {}
      countedItems.forEach((item) => {
        //@ts-ignore
        const slotType = item.slotType.toString()
        const existingItems = groupedItems[slotType] || []
        if (!existingItems.some((existingItem) => existingItem.id === item.id)) {
          //@ts-ignore
          const newItem = { ...item, count: counts[item.id] }
          groupedItems[slotType] = [...existingItems, newItem]
        }
      })

      return groupedItems
    }, [items, previewItems, isDNA])

    // scroll to previously selected item
    useEffect(() => {
      if (searchParams.get('slot')) {
        // @ts-ignore
        selectedElementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, [selectedElementRef, searchParams])

    const { setLerping, setCameraMode } = useCameraStorage()
    useEffect(() => {
      if (selectedItem) {
        setLerping(true)
        setCameraMode(selectedItem.slotType as SlotType)
      } else {
        setLerping(true)
        setCameraMode(SlotType.Unknown)
      }
    }, [selectedItem])

    const nextStep = (item: InventoryItem, color?: string) => {
      // selected item and go to color variants
      if (item.slotType !== SlotType.Skins && !selectedItem && item.enableVariantColors) {
        setSelectedItem(item)
        equipItem({ ...item, variantColor: color })
        return
      }
      // only selected item if color variant is not enabled
      isDNA ? setSelectedOnboardingSkin(item) : equipItem({ ...item, variantColor: color })
    }

    const equipInventory = async () => {
      equipInventoryItemsBatch({
        payload: equipItemsPayload
      })
    }

    const slotTypesToMap = isDNATraits ? DNA_TRAITS_SLOT_TYPES : isDNA ? DNA_SLOT_TYPES : TENANT_CONFIG.sortedSlots

    if (isMobile) {
      return (
        <MobileInventory
          groupedItems={groupedItems}
          mint={equipInventory}
          isLoading={isEquipping}
          nextStep={nextStep}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          hideActionMenu={hideActionMenu}
          slotTypesToMap={slotTypesToMap}
        />
      )
    }
    return isEquipping || isEquipSuccess ? (
      <Stack justify='center' align='center' ta='center' mt='md'>
        <AvatarPreview />
        <Stack gap='0'>
          {isEquipping ? (
            <>
              <Title order={2}>{t('Equipping')}</Title>
              <Text>
                {t('Your avatar is being equipped')}. <br /> {t('Confirmation expected in 3s')}...
              </Text>
            </>
          ) : (
            <>
              <Title order={2}>{t('Success')}</Title>
              <Text>
                {t('Your avatar was successfully equipped')}. <br />
                {t('Close this menu and check it out')}.
              </Text>
            </>
          )}
        </Stack>
        <Box w='100%' bottom={0} left={0} pos={{ base: 'fixed', md: 'absolute' }}>
          <Divider mb='md' />
          <Group justify='space-between' px='sm' mb='md'>
            <Button
              variant='outline'
              miw={100}
              onClick={() => {
                setIsEquipSuccess(false)
                navigate('/')
              }}
            >
              {t('Close')}
            </Button>
            {isAbleToShareAvatar && !isEquipping && isEquipSuccess ? (
              <SocialSharingModal variant='avatar' asButton buttonText='Share' />
            ) : (
              <Button onClick={equipInventory} disabled loading={isEquipping}>
                {t('Equip Gear')}
              </Button>
            )}
          </Group>
        </Box>
      </Stack>
    ) : (
      <>
        <Stack w='full' gap='md' py='md'>
          {isDNATraits ? (
            <Tabs defaultValue={SlotType[slotTypesToMap[0]]}>
              <Tabs.List>
                {slotTypesToMap.map((key: any) => (
                  <Tabs.Tab value={SlotType[key]} key={SlotType[key]}>
                    {SlotType[key].replace(/([a-z])([A-Z])/g, '$1 $2')}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              {slotTypesToMap.map((key: any) => {
                const targetSlotType = SlotType[key] as any
                const itemsPerGroup = groupedItems[SlotType[targetSlotType] as any]
                return (
                  <Tabs.Panel value={SlotType[key]} key={SlotType[key]} mt='md'>
                    <ScrollArea.Autosize h='65vh' maw='510px' className='overflow-hidden'>
                      <Group wrap='wrap'>
                        {itemsPerGroup ? (
                          itemsPerGroup.map((inventoryItem: InventoryItem, index: number) => (
                            <InventorySlot
                              key={`${inventoryItem.id}-${index}`}
                              image={inventoryItem.imageUrl}
                              count={inventoryItem.count}
                              onClick={() => (inventoryItem.isEquipped ? clearCategory(key) : nextStep(inventoryItem))}
                              isActive={
                                isDNA
                                  ? selectedOnboardingSkin?.glbUrl === inventoryItem.glbUrl
                                  : inventoryItem.isEquipped
                              }
                            />
                          ))
                        ) : (
                          <InventorySlot isLoading={isInventoryItemsLoading} />
                        )}
                      </Group>
                    </ScrollArea.Autosize>
                  </Tabs.Panel>
                )
              })}
            </Tabs>
          ) : !selectedItem ? (
            slotTypesToMap.map((key: any) => {
              const targetSlotType = SlotType[key] as any
              const itemsPerGroup = groupedItems[SlotType[targetSlotType] as any]

              return (
                <Stack gap='sm' key={key} ref={searchParams.get('slot') === SlotType[key] ? selectedElementRef : null}>
                  <Text>{SlotType[key]}</Text>
                  <Group wrap='wrap'>
                    {itemsPerGroup ? (
                      itemsPerGroup.map((inventoryItem: InventoryItem, index: number) => (
                        <InventorySlot
                          key={`${inventoryItem.id}-${index}`}
                          image={inventoryItem.imageUrl}
                          count={inventoryItem.count}
                          onClick={() => (inventoryItem.isEquipped ? clearCategory(key) : nextStep(inventoryItem))}
                          isActive={
                            isDNA ? selectedOnboardingSkin?.glbUrl === inventoryItem.glbUrl : inventoryItem.isEquipped
                          }
                        />
                      ))
                    ) : (
                      <InventorySlot isLoading={isInventoryItemsLoading} />
                    )}
                  </Group>
                </Stack>
              )
            })
          ) : (
            <Stack gap='sm'>
              <Group gap='0'>
                <button onClick={() => setSelectedItem(null)}>
                  <Text size='sm' tt='capitalize'>
                    {SlotType[selectedItem.slotType as SlotType]}
                  </Text>
                </button>{' '}
                <Icon size='sm' color='dimmed' name='ChevronRight' />
                <Text size='sm' tt='capitalize'>
                  {t('Options')}
                </Text>
              </Group>
              <Group gap='sm' wrap='wrap'>
                <InventorySlot
                  image='/images/null.png'
                  onClick={() => unequipItem(selectedItem)}
                  className='p-4 opacity-20'
                />
                {VARIANT_COLORS.map((color: string) => (
                  <InventorySlot
                    key={color}
                    image={selectedItem.imageUrl}
                    onClick={() => nextStep(selectedItem, color)}
                    className='border-2'
                    style={{ borderColor: color }}
                  />
                ))}
              </Group>
            </Stack>
          )}
        </Stack>
        {!hideActionMenu && (
          <Box pos='sticky' bottom={0}>
            <Divider mx={-16} />
            <EnhancedPaper py={16} mb={-16} withBg>
              <Group justify='space-between'>
                {selectedItem ? (
                  <Button
                    variant='outline'
                    miw={100}
                    onClick={() => {
                      setSearchParams({ slot: SlotType[selectedItem.slotType as SlotType] })
                      setSelectedItem(null)
                    }}
                  >
                    {t('Back')}
                  </Button>
                ) : (
                  <Button variant='outline' miw={100} onClick={() => navigate('/')}>
                    {t('Close')}
                  </Button>
                )}

                {!selectedItem && (
                  <Button onClick={equipInventory} loading={isEquipping}>
                    {t('Equip Gear')}
                  </Button>
                )}
              </Group>
            </EnhancedPaper>
          </Box>
        )}
      </>
    )
  }
)
InventoryModule.displayName = 'InventoryModule'
export default InventoryModule
